import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Tab, Nav, Card, Button, Badge, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

const IITJAMChemistryPage = () => {
  const [syllabusData, setSyllabusData] = useState([]);
  const [papersData, setPapersData] = useState([]);
  const [booksData, setBooksData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Simulate fetching data from database
  useEffect(() => {
    // In a real application, these would be API calls
    const fetchData = () => {
      setTimeout(() => {
        setSyllabusData([
          { 
            id: 1, 
            title: 'Physical Chemistry', 
            topics: ['Chemical Thermodynamics', 'Chemical Equilibrium', 'Phase Equilibria', 'Electrochemistry', 'Chemical Kinetics', 'Quantum Chemistry', 'Spectroscopy'] 
          },
          { 
            id: 2, 
            title: 'Organic Chemistry', 
            topics: ['Reaction Mechanisms', 'Stereochemistry', 'Organic Synthesis', 'Reactive Intermediates', 'Named Reactions', 'Heterocyclic Compounds'] 
          },
          { 
            id: 3, 
            title: 'Inorganic Chemistry', 
            topics: ['Periodic Table', 'Coordination Chemistry', 'Organometallic Compounds', 'Bioinorganic Chemistry', 'Solid State Chemistry'] 
          },
          { 
            id: 4, 
            title: 'Analytical Chemistry', 
            topics: ['Chromatographic Techniques', 'Spectroscopic Methods', 'Error Analysis', 'Statistical Treatment', 'Electroanalytical Methods'] 
          },
        ]);
        
        setPapersData([
          { id: 1, year: '2024', paperLink: '#', answerKeyLink: '#', difficulty: 'High' },
          { id: 2, year: '2023', paperLink: '#', answerKeyLink: '#', difficulty: 'Medium' },
          { id: 3, year: '2022', paperLink: '#', answerKeyLink: '#', difficulty: 'Medium' },
          { id: 4, year: '2021', paperLink: '#', answerKeyLink: '#', difficulty: 'High' },
          { id: 5, year: '2020', paperLink: '#', answerKeyLink: '#', difficulty: 'Medium' },
          { id: 6, year: '2019', paperLink: '#', answerKeyLink: '#', difficulty: 'Low' },
          { id: 7, year: '2018', paperLink: '#', answerKeyLink: '#', difficulty: 'Medium' },
          { id: 8, year: '2017', paperLink: '#', answerKeyLink: '#', difficulty: 'High' },
        ]);
        
        setBooksData([
          { 
            id: 1, 
            title: 'Physical Chemistry', 
            author: 'Peter Atkins & Julio de Paula', 
            coverImg: '/api/placeholder/200/300', 
            rating: 4.8,
            category: 'Physical Chemistry'
          },
          { 
            id: 2, 
            title: 'Organic Chemistry', 
            author: 'Jonathan Clayden, Nick Greeves, Stuart Warren', 
            coverImg: '/api/placeholder/200/300', 
            rating: 4.9,
            category: 'Organic Chemistry'
          },
          { 
            id: 3, 
            title: 'Concise Inorganic Chemistry', 
            author: 'J.D. Lee', 
            coverImg: '/api/placeholder/200/300', 
            rating: 4.7,
            category: 'Inorganic Chemistry'
          },
          { 
            id: 4, 
            title: 'Modern Analytical Chemistry', 
            author: 'David Harvey', 
            coverImg: '/api/placeholder/200/300', 
            rating: 4.5,
            category: 'Analytical Chemistry'
          },
          { 
            id: 5, 
            title: 'Problems in Physical Chemistry', 
            author: 'Narendra Awasthi', 
            coverImg: '/api/placeholder/200/300', 
            rating: 4.6,
            category: 'Problem Solving'
          },
        ]);
        
        setLoading(false);
      }, 1000);
    };
    
    fetchData();
  }, []);

  const internships = [
    {
      id: 1,
      title: "Computational Chemistry Research",
      description: "Work with advanced computational tools to model chemical reactions and predict molecular properties using AI and machine learning approaches.",
      duration: "4 months",
      stipend: "₹16,000/month",
      requirements: ["Basic programming skills", "Physical chemistry knowledge", "Understanding of quantum mechanics fundamentals"],
      icon: "🧪"
    },
    {
      id: 2,
      title: "Analytical Methods Development",
      description: "Apply AI-driven techniques to develop new analytical methods for complex chemical analysis and quality control in pharmaceutical industry.",
      duration: "6 months",
      stipend: "₹20,000/month",
      requirements: ["Experience with analytical instruments", "Data analysis skills", "Good laboratory practices"],
      icon: "⚗️"
    },
    {
      id: 3,
      title: "Materials Chemistry Innovation",
      description: "Join our materials innovation lab to develop and characterize novel materials using AI prediction models and advanced synthesis techniques.",
      duration: "5 months",
      stipend: "₹18,000/month",
      requirements: ["Solid state chemistry background", "Interest in materials science", "Basic understanding of characterization techniques"],
      icon: "🔬"
    }
  ];

  return (
    <div className="iit-jam-chemistry-page">
      {/* Hero Section */}
      <section className="hero-section text-white py-5 my-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="hero-content">
                <Badge bg="light" text="dark" className="mb-2">Top Ranked Institute</Badge>
                <h1 className="display-4 fw-bold mb-3">Excel in IIT-JAM Chemistry</h1>
                <p className="lead mb-4">Comprehensive coaching with practical experience through cutting-edge chemistry internships. Join the program that has produced over 500+ successful IIT-JAM candidates.</p>
                <div className="d-flex gap-3 flex-wrap">
                  <Button variant="light" size="lg" className="fw-bold">Apply Now</Button>
                  <Button variant="outline-light" size="lg">Download Brochure</Button>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="hero-image text-center position-relative">
                <div className="hero-shape-1"></div>
                <div className="hero-shape-2"></div>
                <img src="/api/placeholder/500/350" alt="Chemistry lab with students" className="img-fluid rounded-lg shadow position-relative z-index-1" />
                <div className="stats-card">
                  <div className="stats-item">
                    <h3>95%</h3>
                    <p>Success Rate</p>
                  </div>
                  <div className="stats-item">
                    <h3>12+</h3>
                    <p>Years Experience</p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Coaching Information Section with Tabs */}
      <section className="coaching-section py-5">
        <Container>
          <div className="section-heading text-center mb-5">
            <Badge bg="primary" className="mb-2">World-Class Program</Badge>
            <h2 className="display-6 fw-bold">IIT-JAM Chemistry Preparation</h2>
            <p className="text-muted mx-auto" style={{ maxWidth: '700px' }}>Our meticulously designed program covers every aspect of the IIT-JAM Chemistry syllabus, preparing you for one of India's most competitive exams</p>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" className="mb-3">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="text-muted">Loading course materials...</p>
            </div>
          ) : (
            <Tab.Container id="coaching-tabs" defaultActiveKey="syllabus">
              <Nav variant="pills" className="nav-tabs-custom justify-content-center mb-4">
                <Nav.Item>
                  <Nav.Link eventKey="syllabus">
                    <i className="bi bi-journal-text me-2"></i>Syllabus
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="papers">
                    <i className="bi bi-file-earmark-text me-2"></i>Previous Papers
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="books">
                    <i className="bi bi-book me-2"></i>Reference Books
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="program">
                    <i className="bi bi-calendar-check me-2"></i>Our Program
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              
              <div className="tab-content-wrapper p-4 bg-white rounded-lg shadow-sm">
                <Tab.Content>
                  {/* Syllabus Tab */}
                  <Tab.Pane eventKey="syllabus">
                    <div className="syllabus-container">
                      <Row>
                        {syllabusData.map(unit => (
                          <Col md={6} key={unit.id} className="mb-4">
                            <Card className="h-100 border-0 shadow-sm syllabus-card">
                              <Card.Header className="bg-gradient-primary text-white">
                                <h3 className="h5 mb-0">{unit.title}</h3>
                              </Card.Header>
                              <Card.Body className="p-4">
                                <ul className="topic-list">
                                  {unit.topics.map((topic, index) => (
                                    <li key={index} className="mb-2">
                                      <div className="d-flex align-items-center">
                                        <span className="topic-icon me-2">•</span>
                                        <span>{topic}</span>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Tab.Pane>
                  
                  {/* Previous Papers Tab */}
                  <Tab.Pane eventKey="papers">
                    <div className="papers-container">
                      <div className="table-responsive">
                        <table className="table table-hover papers-table">
                          <thead>
                            <tr className="table-light">
                              <th>Year</th>
                              <th>Difficulty Level</th>
                              <th>Question Paper</th>
                              <th>Answer Key</th>
                              <th>Analysis</th>
                            </tr>
                          </thead>
                          <tbody>
                            {papersData.map(paper => (
                              <tr key={paper.id}>
                                <td className="fw-bold">{paper.year}</td>
                                <td>
                                  <Badge bg={
                                    paper.difficulty === 'High' ? 'danger' : 
                                    paper.difficulty === 'Medium' ? 'warning' : 'success'
                                  }>
                                    {paper.difficulty}
                                  </Badge>
                                </td>
                                <td>
                                  <Button variant="outline-primary" size="sm" href={paper.paperLink}>
                                    <i className="bi bi-download me-1"></i> Download
                                  </Button>
                                </td>
                                <td>
                                  <Button variant="outline-success" size="sm" href={paper.answerKeyLink}>
                                    <i className="bi bi-file-earmark-check me-1"></i> Solutions
                                  </Button>
                                </td>
                                <td>
                                  <Button variant="link" size="sm">
                                    <i className="bi bi-bar-chart me-1"></i> View
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Tab.Pane>
                  
                  {/* Reference Books Tab */}
                  <Tab.Pane eventKey="books">
                    <div className="books-container">
                      <Row className="g-4">
                        {booksData.map(book => (
                          <Col md={6} lg={4} key={book.id}>
                            <Card className="h-100 book-card shadow-sm">
                              <Row className="g-0">
                                <Col xs={4} className="book-cover-col">
                                  <div className="book-cover-wrapper">
                                    <Badge className="category-badge" bg="info">{book.category}</Badge>
                                    <img src={book.coverImg} alt={book.title} className="img-fluid rounded-start book-cover" />
                                  </div>
                                </Col>
                                <Col xs={8}>
                                  <Card.Body>
                                    <Card.Title className="h5 mb-1">{book.title}</Card.Title>
                                    <Card.Text className="text-muted small mb-2">{book.author}</Card.Text>
                                    <div className="d-flex align-items-center mb-2">
                                      <div className="rating-stars me-2">
                                        {'★'.repeat(Math.floor(book.rating))}
                                        {book.rating % 1 !== 0 ? '½' : ''}
                                        {'☆'.repeat(5 - Math.ceil(book.rating))}
                                      </div>
                                      <span className="rating-value">{book.rating}/5.0</span>
                                    </div>
                                    <Button variant="outline-primary" size="sm" className="mt-2">Read Reviews</Button>
                                  </Card.Body>
                                </Col>
                              </Row>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Tab.Pane>
                  
                  {/* Our Program Tab */}
                  <Tab.Pane eventKey="program">
                    <Row>
                      <Col lg={6} className="mb-4">
                        <div className="program-features">
                          <h3 className="h4 mb-4">Program Features</h3>
                          <div className="feature-grid">
                            <div className="feature-card">
                              <div className="feature-icon">👨‍🏫</div>
                              <h4>PhD Faculty</h4>
                              <p>Learn from doctorate professors with research experience</p>
                            </div>
                            <div className="feature-card">
                              <div className="feature-icon">🧪</div>
                              <h4>Practical Labs</h4>
                              <p>Weekly hands-on sessions in fully-equipped chemistry labs</p>
                            </div>
                            <div className="feature-card">
                              <div className="feature-icon">📝</div>
                              <h4>Test Series</h4>
                              <p>Weekly assessment with detailed performance analysis</p>
                            </div>
                            <div className="feature-card">
                              <div className="feature-icon">💻</div>
                              <h4>Online Portal</h4>
                              <p>24/7 access to video lectures and practice resources</p>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="program-timeline">
                          <h3 className="h4 mb-4">Program Timeline</h3>
                          <div className="timeline">
                            <div className="timeline-item">
                              <div className="timeline-dot"></div>
                              <div className="timeline-content">
                                <div className="timeline-date">Month 1-2</div>
                                <h4 className="timeline-title">Foundation Phase</h4>
                                <p>Building strong conceptual understanding of all core topics</p>
                              </div>
                            </div>
                            <div className="timeline-item">
                              <div className="timeline-dot"></div>
                              <div className="timeline-content">
                                <div className="timeline-date">Month 3-5</div>
                                <h4 className="timeline-title">Advanced Concepts</h4>
                                <p>Focusing on complex topics and problem-solving techniques</p>
                              </div>
                            </div>
                            <div className="timeline-item">
                              <div className="timeline-dot"></div>
                              <div className="timeline-content">
                                <div className="timeline-date">Month 6-7</div>
                                <h4 className="timeline-title">Intensive Problem Solving</h4>
                                <p>Mastering numerical and analytical problem solving</p>
                              </div>
                            </div>
                            <div className="timeline-item">
                              <div className="timeline-dot"></div>
                              <div className="timeline-content">
                                <div className="timeline-date">Month 8-9</div>
                                <h4 className="timeline-title">Mock Tests & Revision</h4>
                                <p>Full-length JAM simulations and comprehensive revision</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Tab.Pane>
                </Tab.Content>
              </div>
            </Tab.Container>
          )}
        </Container>
      </section>

      {/* Internship Section */}
      <section className="internship-section py-5">
        <Container>
          <div className="section-heading text-center mb-5">
            <Badge bg="success" className="mb-2">Career Advancement</Badge>
            <h2 className="display-6 fw-bold">Chemistry + AI Internship Programs</h2>
            <p className="text-muted mx-auto" style={{ maxWidth: '700px' }}>Combined with our IIT-JAM preparation, these internships provide practical experience in cutting-edge chemistry applications</p>
          </div>

          <Row className="g-4">
            {internships.map(internship => (
              <Col md={6} lg={4} key={internship.id}>
                <Card className="h-100 internship-card">
                  <div className="internship-icon">{internship.icon}</div>
                  <Card.Body>
                    <Card.Title className="h4 mb-3">{internship.title}</Card.Title>
                    <Card.Text className="mb-4">{internship.description}</Card.Text>
                    
                    <div className="internship-details mb-4">
                      <div className="detail-item">
                        <span className="detail-label">Duration</span>
                        <span className="detail-value">{internship.duration}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Stipend</span>
                        <span className="detail-value">{internship.stipend}</span>
                      </div>
                    </div>
                    
                    <div className="requirements">
                      <h5 className="h6 requirements-title">Requirements</h5>
                      <ul className="requirements-list">
                        {internship.requirements.map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  </Card.Body>
                  <Card.Footer className="bg-transparent border-0 pb-4">
                    <Button variant="primary" className="w-100">Apply for Internship</Button>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>

          <div className="internship-approach mt-5 bg-light p-4 rounded-lg shadow-sm">
            <Row className="align-items-center">
              <Col lg={5} className="mb-4 mb-lg-0">
                <div className="approach-content p-3">
                  <h3 className="h4 mb-4">Our Integrated Learning Approach</h3>
                  <div className="approach-features">
                    <div className="approach-feature d-flex mb-3">
                      <div className="approach-icon me-3">🔄</div>
                      <div>
                        <h4 className="h5 mb-1">Theory to Practice</h4>
                        <p className="mb-0">Apply complex concepts from your JAM preparation in real-world scenarios</p>
                      </div>
                    </div>
                    <div className="approach-feature d-flex mb-3">
                      <div className="approach-icon me-3">🧠</div>
                      <div>
                        <h4 className="h5 mb-1">AI Integration</h4>
                        <p className="mb-0">Learn how modern computational methods are transforming chemistry research</p>
                      </div>
                    </div>
                    <div className="approach-feature d-flex">
                      <div className="approach-icon me-3">📈</div>
                      <div>
                        <h4 className="h5 mb-1">Publication Opportunities</h4>
                        <p className="mb-0">Contribute to research papers and build your academic portfolio</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg={7}>
                <div className="approach-stats">
                  <Row className="g-3">
                    <Col md={4}>
                      <div className="stat-card text-center">
                        <div className="stat-value">92%</div>
                        <div className="stat-label">Placement Rate</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="stat-card text-center">
                        <div className="stat-value">40+</div>
                        <div className="stat-label">Industry Partners</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="stat-card text-center">
                        <div className="stat-value">15+</div>
                        <div className="stat-label">Research Publications</div>
                      </div>
                    </Col>
                  </Row>
                  <div className="approach-image mt-4">
                    <img src="/api/placeholder/600/300" alt="Students in chemistry lab" className="img-fluid rounded" />
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default IITJAMChemistryPage;

// Add CSS for styling
export const ChemistryPageCSS = `
/* General Styles */
.iit-jam-chemistry-page {
  font-family: 'Inter', sans-serif;
  color: #333;
}

.section-heading {
  margin-bottom: 3rem;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(120deg, #051937, #004d7a, #008793);
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
}

.hero-content h1 {
  font-weight: 800;
  margin-bottom: 1.5rem;
}

.hero-image {
  position: relative;
}

.hero-image img {
  border-radius: 12px;
  transition: transform 0.3s ease;
}

.hero-image:hover img {
  transform: translateY(-5px);
}

.hero-shape-1,
.hero-shape-2 {
  position: absolute;
  border-radius: 50%;
  opacity: 0.2;
  z-index: 0;
}

.hero-shape-1 {
  width: 150px;
  height: 150px;
  background: linear-gradient(45deg, #ff5e62, #ff9966);
  top: -30px;
  right: 10%;
}

.hero-shape-2 {
  width: 100px;
  height: 100px;
  background: linear-gradient(45deg, #00d2ff, #3a7bd5);
  bottom: -20px;
  left: 10%;
}

.stats-card {
  position: absolute;
  right: -20px;
  bottom: 30px;
  background-color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 15px;
  display: flex;
  width: 180px;
  z-index: 2;
}

.stats-item {
  flex: 1;
  text-align: center;
}

.stats-item h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #004d7a;
}

.stats-item p {
  margin: 0;
  font-size: 0.8rem;
  color: #666;
}

/* Coaching Section */
.coaching-section {
  background-color: #f8f9fa;
}

.nav-tabs-custom {
  display: inline-flex;
  background-color: #fff;
  border-radius: 999px;
  padding: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.nav-tabs-custom .nav-link {
  border-radius: 999px;
  padding: 10px 20px;
  color: #555;
  font-weight: 500;
  border: none;
  transition: all 0.3s;
}

.nav-tabs-custom .nav-link.active {
  background-color: #004d7a;
  color: white;
}

.tab-content-wrapper {
  background-color: #fff;
  border-radius: 12px;
}

/* Syllabus Tab */
.syllabus-card {
  transition: transform 0.3s;
}

.syllabus-card:hover {
  transform: translateY(-5px);
}

.syllabus-card .card-header {
  background: linear-gradient(45deg, #004d7a, #008793);
  color: white;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  padding: 15px 20px;
}

.topic-list {
  list-style-type: none;
  padding-left: 0;
}

.topic-icon {
  color: #004d7a;
  font-weight: bold;
  font-size: 18px;
}

/* Papers Tab */
.papers-table th, .papers-table td {
  vertical-align: middle;
}

/* Books Tab */
.book-card {
  overflow: hidden;
  transition: transform 0.3s;
}

.book-card:hover {
  transform: translateY(-5px);
}

.book-cover-wrapper {
  position: relative;
  height: 100%;
}

.book-cover {
  height: 100%;
  object-fit: cover;
}

.category-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  font-size: 0.7rem;
}

.rating-stars {
  color: #ffc107;
  font-size: 14px;
}

.rating-value {
  font-size: 14px;
  font-weight: 600;
}

/* Program Tab */
.feature-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.feature-card {
  background-color: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  transition: all 0.3s ease;
}

.feature-card:hover {
  background-color: #e9ecef;
  transform: translateY(-3px);
}

.feature-icon {
  font-size: 2rem;
  margin-bottom: 15px;
}

.feature-card h4 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.feature-card p {
  font-size: 0.9rem;
  margin-bottom: 0;
  color: #666;
}

.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline:before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 2px;
  height: 100%;
  background-color: #004d7a;
}

.timeline-item {
  position: relative;
  padding-bottom: 25px;
}

.timeline-dot {
  position: absolute;
  left: -39px;
  top: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #004d7a;
  border: 4px solid #fff;
  box-shadow: 0 0 0 2px #004d7a;
}

.timeline-date {
  display: inline-block;
  padding: 4px 10px;
  background-color: #004d7a;
  color: white;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 10px;
}

.timeline-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 5px;
}

/* Internship Section */
.internship-card {
  border-radius: 12px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
}

.internship-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.internship-icon {
  font-size: 2.5rem;
  position: absolute;
  right: 20px;
  top: 20px;
}

.internship-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 1.5rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-label {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 4px;
}

.detail-value {
  font-weight: 600;
  color: #004d7a;
}

.requirements-title {
  position: relative;
  padding-bottom: 10px;
  margin-bottom: 15px;
}

.requirements-title:after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 50px;
  height: 2px;
  background-color: #004d7a;
}

.requirements-list {
  padding-left: 20px;
  margin-bottom: 0;
}

.requirements-list li {
  margin-bottom: 6px;
  position: relative;
}

.approach-icon {
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-card {
  background-color: white;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s;
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #004d7a;
}

.stat-label {
  font-size: 0.85rem;
  color: #6c757d;
}

/* Responsive Styles */
@media (max-width: 992px) {
  .feature-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-card {
    position: relative;
    right: auto;
    bottom: auto;
    margin-top: 20px;
    width: 100%;
  }
  
  .hero-section {
    padding: 4rem 0;
  }
}

@media (max-width: 768px) {
  .nav-tabs-custom {
    display: flex;
    width: 100%;
    overflow-x: auto;
    padding: 3px;
  }
  
  .nav-tabs-custom .nav-link {
    padding: 8px 15px;
    white-space: nowrap;
  }
  
  .internship-details {
    grid-template-columns: 1fr;
  }
  
  .timeline {
    padding-left: 20px;
  }
  
  .timeline-dot {
    left: -29px;
    width: 16px;
    height: 16px;
  }
}

/* Create scrollable containers for content-heavy tabs */
.syllabus-container, .papers-container, .books-container {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 5px;
}

.syllabus-container::-webkit-scrollbar,
.papers-container::-webkit-scrollbar,
.books-container::-webkit-scrollbar {
  width: 6px;
}

.syllabus-container::-webkit-scrollbar-thumb,
.papers-container::-webkit-scrollbar-thumb,
.books-container::-webkit-scrollbar-thumb {
  background-color: #b8c2cc;
  border-radius: 3px;
}

.syllabus-container::-webkit-scrollbar-track,
.papers-container::-webkit-scrollbar-track,
.books-container::-webkit-scrollbar-track {
  background-color: #f8f9fa;
}

/* Bootstrap Icons - Import in your HTML or add the CDN */
@import url("https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css");

/* Z-index control */
.z-index-1 {
  z-index: 1;
}

/* Additional helper classes */
.rounded-lg {
  border-radius: 12px !important;
}

.bg-gradient-primary {
  background: linear-gradient(45deg, #004d7a, #008793);
}
  `;